import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { activateCategory, activateParentCategory, closeModal, filteredCategories, searchPhrase } from './signals.ts';
import { Modal } from 'components/modal/Modal.tsx';
import { localize } from 'i18n/localize.ts';
import { CategorySearchResult } from './CategorySearchResult.tsx';
import { CategoryItem } from 'components/modal/categories/CategoryItem.tsx';
import { cn } from 'utils/cn.tsx';
import { urlEncoder } from 'utils/url.ts';
import { filters } from 'signals/filters.ts';
import { ModalHeader } from 'components/modal/ModalHeader.tsx';
import type { CategoryData } from 'service/category/model.ts';
import { Pill } from 'components/atoms/Pill';
import { Icon } from 'components/atoms/Icon';
import { useComputed, useSignal } from '@preact/signals';
import { activeCategory, displayCategoriesSorted } from 'signals/category.ts';

type Props = {
	forceLeafCategory?: boolean;
	noLinks?: boolean;
	onSelect?: (category: null | CategoryData) => void;
	onClose?: () => void;
	onClickAway?: () => void;
	children?: ComponentChildren;
};

const $t = localize();

/**
 * Modal is not expected to be shown anywhere else than on the homepage. Some cuts were made due to this presumption.
 * @param forceLeafCategory - If true, the user will be forced to select a leaf category by hiding a button at the
 * bottom that allows to select the current non-leaf category.
 * @param noLinks
 * @param onSelect
 * @param onClose
 * @param onClickAway
 */
export function CategoriesModal({
	forceLeafCategory = false,
	noLinks = false,
	onSelect = () => {},
	onClose = closeModal,
	onClickAway = closeModal,
}: Props) {
	const filteredFilteredCategories = useComputed(() => {
		return forceLeafCategory
			? filteredCategories.value?.filter((category) => category.children.length === 0)
			: filteredCategories.value;
	});

	const searchingModalHeight = useSignal(600);
	const modalRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (modalRef.current) {
			searchingModalHeight.value = modalRef.current.clientHeight;
		}
	}, [activeCategory.value]);

	useLayoutEffect(() => {
		if (!modalRef.current) {
			return;
		}

		if (searchPhrase.peek()) {
			modalRef.current.style.height = `${searchingModalHeight.value}px`;
		} else {
			modalRef.current.style.height = '';
		}
	}, [searchPhrase.value]);

	return (
		<Modal
			className="bg-neutral-white max-w-screen-lg w-full p-4 pb-0 pt-3 md:px-12 mdp:px-8 md:pb-8 md:pt-6 overflow-auto"
			onClickAway={onClickAway}
			onClose={onClose}
			isMobileFullscreen={true}
			forwardedRef={modalRef}
		>
			{/* Header */}
			<div class="mb-6 sticky mx-2 md:mx-0">
				<ModalHeader
					titleClassName={cn(`text-base md:text-3xl`, {
						'cursor-pointer': activeCategory.value,
					})}
					header={(titleClassName, onHeaderClick) => (
						<div class="flex items-center justify-between flex-grow">
							<h1 class={cn('mr-4 text-base font-bold', titleClassName)} onClick={onHeaderClick}>
								{activeCategory.value && <span class="mr-4 font-bold">{`<-`}</span>}
								<span>
									{activeCategory.value ? activeCategory.value?.name : $t.categories.allCategories}
								</span>
							</h1>
							{!forceLeafCategory && (
								<a
									tabIndex={-1}
									class="mr-4 hidden md:block"
									href={
										noLinks
											? undefined
											: urlEncoder({
													...filters.value,
													category: activeCategory.value || undefined,
												})
									}
								>
									<Pill
										pillType="primary"
										className="p-3 px-6"
										onClick={() => {
											onSelect(activeCategory.value);
											onClose();
										}}
									>
										<span class="font-bold text-sm">
											{activeCategory.value
												? $t.categories.showOffers
												: $t.categories.showAllOffers}
										</span>
									</Pill>
								</a>
							)}
						</div>
					)}
					onHeaderClick={activateParentCategory}
					onClose={onClose}
				/>
			</div>
			{/* Search input */}
			<div class="relative mb-6">
				<input
					type="text"
					class="rounded-full p-3 w-full pl-12 border border-gray-input text-sm placeholder:text-dark-blue-50"
					placeholder={$t.categories.search}
					value={searchPhrase}
					onInput={(event: InputEvent) => {
						searchPhrase.value = (event.target as HTMLInputElement).value;
					}}
					autocomplete="off"
				/>
				<button onClick={() => (searchPhrase.value = '')}>
					<Icon
						type="close"
						title={$t.searchBox.clearAction}
						size={24}
						className={cn(
							'absolute right-3 top-1/2 -translate-y-1/2 text-dark-blue-25',
							searchPhrase.value === '' && 'hidden',
						)}
					/>
				</button>
				<Icon
					type="search"
					title={$t.searchBox.searchAction}
					size={24}
					className="mx-2 absolute left-2 top-1/2 -translate-y-1/2 text-dark-blue-25"
				/>
			</div>
			{/* List of categories/subcategories of active category shown if no search criteria are present. */}
			{searchPhrase.value === '' && (
				<div class="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-[16px_32px]">
					{displayCategoriesSorted.value.map((category) => {
						const categoryItem = (
							<CategoryItem
								category={category}
								onClick={() => {
									if (!forceLeafCategory || (forceLeafCategory && category.children.length === 0)) {
										onSelect(category);
									}
									activateCategory(category, {
										onClose,
									});
								}}
							/>
						);
						return category.children.length ? (
							categoryItem
						) : noLinks ? (
							categoryItem
						) : (
							<a tabindex={-1} href={urlEncoder({ ...filters.value, category: category })}>
								{categoryItem}
							</a>
						);
					})}
				</div>
			)}
			{/* If search is active, show the results if there are any or 'nothing found' message. */}
			{searchPhrase.value &&
				filteredFilteredCategories.value &&
				((!filteredFilteredCategories.value.length && (
					<div class="text-dark-blue-50">{$t.categories.foundNone}</div>
				)) || (
					<a href={noLinks ? undefined : '/'}>
						<div class="grid grid-cols-1 gap-3 md:gap-4">
							{noLinks
								? filteredFilteredCategories.value.map((category) => (
										<CategorySearchResult
											category={category}
											key={category.id}
											onClick={() => {
												onSelect(category);
												onClose();
											}}
										/>
									))
								: filteredFilteredCategories.value.map((category) => (
										<a href={urlEncoder({ ...filters.value, category: category })}>
											<CategorySearchResult
												category={category}
												key={category.id}
												onClick={() => {
													onSelect(category);
													onClose();
												}}
											/>
										</a>
									))}
						</div>
					</a>
				))}
			{!forceLeafCategory && (
				<div class="sticky bottom-0 pb-4 mt-4 md:hidden mx-auto flex justify-center !bg-gradient-to-b from-[#00000000] to-[#ffffff]">
					<Pill
						as="a"
						href={
							noLinks
								? undefined
								: urlEncoder({
										...filters.value,
										category: activeCategory.value || undefined,
									})
						}
						pillType="primary"
						className="p-3 px-6 transform-gpu"
						onClick={() => {
							onSelect(activeCategory.value);
							onClose();
						}}
					>
						<span class="font-bold text-sm">
							{activeCategory.value ? $t.categories.showOffers : $t.categories.showAllOffers}
						</span>
					</Pill>
				</div>
			)}
		</Modal>
	);
}
